import React, { useEffect, useRef, useState } from 'react';
import { usePressreleases } from './hooks/usePressreleases';
import LoadMoreButton from '../btnLoadMore';
import { useTranslation } from 'react-i18next';

const MONTH_VALUES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

const PressreleaseArchive = () => {
    const { t, i18n } = useTranslation();
    const itemsPerPage = 12;
    const { pressreleases, getPageNumber, availableYears, currentPageLoaded, loading, hasMore } = usePressreleases(itemsPerPage);

    const [year, setYear] = useState(0);
    const [month, setMonth] = useState(0);
    const [searchText, setSearchText] = useState('');
    const [activeFilter, setActiveFilter] = useState({ year: 0, month: 0, searchText: '' });
    const [shouldFocusStatus, setShouldFocusStatus] = useState(false);
    const statusRef = useRef<HTMLDivElement>(null);
    const shouldFocusLoadedArticle = useRef(false);
    const articleCountBeforeLoad = useRef(0);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const initialPage = parseInt(params.get('page') || '1') || 1;
        const initialYear = parseInt(params.get('year') || '0') || 0;
        const initialMonth = parseInt(params.get('month') || '0') || 0;
        const initialSearch = params.get('q') || '';

        setYear(initialYear);
        setMonth(initialMonth);
        setSearchText(initialSearch);
        setActiveFilter({ year: initialYear, month: initialMonth, searchText: initialSearch });
        getPageNumber(initialPage, initialYear, initialMonth, initialSearch);
    }, []);

    useEffect(() => {
        if (loading || currentPageLoaded <= 1) return;

        const all = document.querySelectorAll('#pressreleasesWrapper .article-link');
        const scrollTarget = all[currentPageLoaded * itemsPerPage - itemsPerPage];
        if (!scrollTarget) return;

        window.scrollTo({
            top: scrollTarget.getBoundingClientRect().top + window.scrollY,
            behavior: 'smooth',
        });
    }, [pressreleases]);

    useEffect(() => {
        if (!shouldFocusLoadedArticle.current || pressreleases.length <= articleCountBeforeLoad.current) return;

        const articles = document.querySelectorAll<HTMLAnchorElement>('#pressreleasesWrapper .article-link');
        articles[articleCountBeforeLoad.current]?.focus();
        shouldFocusLoadedArticle.current = false;
    }, [pressreleases]);

    useEffect(() => {
        if (loading) {
            statusRef.current && (statusRef.current.textContent = t('articles.loading'));
            return;
        }

        if (pressreleases.length === 0 && currentPageLoaded > 0) {
            statusRef.current && (statusRef.current.textContent = t('pressreleases.noResults'));
        } else if (currentPageLoaded > 0) {
            statusRef.current && (statusRef.current.textContent = t('pressreleases.resultsUpdated'));
        }

        if (shouldFocusStatus) {
            statusRef.current?.focus();
            setShouldFocusStatus(false);
        }
    }, [loading, pressreleases.length, currentPageLoaded, shouldFocusStatus, t]);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setActiveFilter({ year, month, searchText });
        setShouldFocusStatus(true);
        getPageNumber(1, year, month, searchText);
        updateUrl(1, year, month, searchText);
    };

    const loadNextPage = () => {
        const next = currentPageLoaded + 1;
        articleCountBeforeLoad.current = pressreleases.length;
        shouldFocusLoadedArticle.current = true;
        getPageNumber(next, activeFilter.year, activeFilter.month, activeFilter.searchText);
        updateUrl(next, activeFilter.year, activeFilter.month, activeFilter.searchText);
    };

    const updateUrl = (page: number, y: number, m: number, q: string) => {
        const params = new URLSearchParams({ page: String(page) });
        if (y > 0) params.set('year', String(y));
        if (m > 0) params.set('month', String(m));
        if (q) params.set('q', q);
        const newUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}?${params}`;
        window.history.pushState({ path: newUrl }, '', newUrl);
    };

    const stripHtml = (html: string) => {
        const div = document.createElement('div');
        div.innerHTML = html;
        return div.textContent || div.innerText || '';
    };

    const getShortDescription = (text: string | null) => {
        const maxLength = 170;
        if (!text) return '';
        if (text.length < maxLength) return text;
        return text.substring(0, maxLength) + ' ...';
    };

    return (
        <>
            <div
                ref={statusRef}
                className="visually-hidden"
                role="status"
                aria-live="polite"
                aria-atomic="true"
                tabIndex={-1}
            />
            <form className="pressrelease-filter" onSubmit={handleSearch}>
                <input
                    type="text"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    placeholder={t('pressreleases.searchPlaceholder')}
                    aria-label={t('pressreleases.searchPlaceholder')}
                />
                <select
                    value={year}
                    onChange={(e) => setYear(parseInt(e.target.value) || 0)}
                    aria-label={t('pressreleases.allYears')}
                >
                    <option value={0}>{t('pressreleases.allYears')}</option>
                    {availableYears.map((y) => (
                        <option key={y} value={y}>{y}</option>
                    ))}
                </select>
                <select
                    value={month}
                    onChange={(e) => setMonth(parseInt(e.target.value) || 0)}
                    aria-label={t('pressreleases.allMonths')}
                >
                    <option value={0}>{t('pressreleases.allMonths')}</option>
                    {MONTH_VALUES.map((m) => (
                        <option key={m} value={m}>{t(`pressreleases.months.${m}`)}</option>
                    ))}
                </select>
                <button type="submit">{t('pressreleases.search')}</button>
            </form>

            {pressreleases.length === 0 && !loading && (
                <p>{t('pressreleases.noResults')}</p>
            )}

            <ul className="articles">
                {pressreleases.map((item) => (
                    <li className="article" key={item.id}>
                        <a className="article-link" href={item.url}>
                            <div className="image-wrapper">
                                {item.imageUrl && (
                                    <img src={item.imageUrl} alt={item.title} loading="lazy" decoding="async" />
                                )}
                            </div>
                            <h3 className="title">{item.title}</h3>
                            <p className="date">
                                {t('articles.published')}: {item.publishedAt ? new Date(item.publishedAt).toLocaleDateString(i18n.language, { year: 'numeric', month: 'long', day: 'numeric' }) : ''}
                            </p>
                            <p className="description">
                                {getShortDescription(item.summary ? stripHtml(item.summary) : null)}
                            </p>
                        </a>
                    </li>
                ))}
            </ul>

            {hasMore && (
                <div style={{ textAlign: 'center' }}>
                    <LoadMoreButton
                        onClick={loadNextPage}
                        text={loading ? t('articles.loading') : t('articles.loadMore')}
                        isLoading={loading}
                    />
                </div>
            )}
        </>
    );
};

export default PressreleaseArchive;
